import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

export const CATEGORIES = ["Immediate Context", "Parallel Passages", "Old Testament Background", "New Testament Fulfillment", "Prophetic Connection", "Thematic Connection", "Supporting Passages"];

let rowSeq = 0;
const rowId = () => `rs-${(rowSeq += 1)}`;

export const toRows = (groups) => (groups || []).flatMap((g) => (g.refs || []).map((r) => ({ _id: rowId(), category: g.group || "", reference: r.reference || "", note: r.note || "" })));

export function toGroups(rows) {
  const out = [];
  rows.filter((r) => r.reference.trim()).forEach((r) => {
    const cat = r.category.trim() || "Supporting Passages";
    let g = out.find((x) => x.group === cat);
    if (!g) out.push((g = { group: cat, refs: [] }));
    g.refs.push({ reference: r.reference.trim(), note: r.note.trim() });
  });
  return out;
}

export function RelatedScripturesEditor({ rows, onChange, testId = "related-editor" }) {
  const update = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...rows, { _id: rowId(), category: rows[rows.length - 1]?.category || CATEGORIES[0], reference: "", note: "" }]);
  const cats = Array.from(new Set([...CATEGORIES, ...rows.map((r) => r.category).filter(Boolean)]));

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="hidden grid-cols-[1fr_1.3fr_2.6fr_auto] gap-2 px-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground sm:grid">
        <span>Category</span><span>Scripture</span><span>Short description</span><span />
      </div>
      {rows.length === 0 && <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground" data-testid={`${testId}-empty`}>No related scriptures yet.</p>}
      {rows.map((r, i) => (
        <div key={r._id || i} className="grid grid-cols-1 gap-2 rounded-xl border border-border bg-background/60 p-2 sm:grid-cols-[1fr_1.3fr_2.6fr_auto] sm:items-start" data-testid={`${testId}-row-${i}`}>
          <select value={r.category} onChange={(e) => update(i, "category", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid={`${testId}-category-${i}`}>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input value={r.reference} onChange={(e) => update(i, "reference", e.target.value)} placeholder="e.g. Isaiah 53:7" className="font-mono text-sm" data-testid={`${testId}-reference-${i}`} />
          <Input value={r.note} onChange={(e) => update(i, "note", e.target.value)} placeholder="Why this passage connects" data-testid={`${testId}-note-${i}`} />
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded-md p-2 text-muted-foreground hover:bg-muted disabled:opacity-30" aria-label="Move up" data-testid={`${testId}-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="rounded-md p-2 text-muted-foreground hover:bg-muted disabled:opacity-30" aria-label="Move down" data-testid={`${testId}-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
            <button type="button" onClick={() => remove(i)} className="rounded-md p-2 text-destructive hover:bg-destructive/10" aria-label="Remove" data-testid={`${testId}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="rounded-full" data-testid={`${testId}-add`}>
        <Plus className="mr-1 h-4 w-4" /> Add scripture
      </Button>
    </div>
  );
}
